import React, { FC, memo, useCallback, useEffect, useRef } from 'react';

import { Responsive } from 'react-grid-layout';

import { DashboardComponent, GridItemLayout, GRID_CONSTANTS } from '../../types';
import DashboardBlock from '../DashboardBlock';
import styles from './DashboardGrid.module.scss';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

/** Зона от края контейнера (px), в которой начинается автоскролл */
const AUTOSCROLL_EDGE = 60;
/** Скорость автоскролла (px за кадр) */
const AUTOSCROLL_SPEED = 15;

interface DashboardGridProps {
  /** Компоненты дашборда */
  dashboardComponents: DashboardComponent[];
  /** Layout для grid */
  layout: GridItemLayout[];
  /** Количество колонок */
  gridCols: number;
  /** Ширина сетки */
  gridWidth: number;
  /** Высота контейнера */
  calculatedHeight: number;
  /** Масштаб */
  scale: number;
  /** Флаг режима drag */
  isDragging: boolean;
  /** Флаг разрешения resize */
  allowResize: boolean;
  /** Обработчик изменения layout */
  onLayoutChange: (layout: GridItemLayout[]) => void;
  /** Обработчик начала drag */
  onDragStart: (layout: GridItemLayout[], oldItem: GridItemLayout, newItem: GridItemLayout) => void;
  /** Обработчик окончания drag */
  onDragStop: (layout: GridItemLayout[], oldItem: GridItemLayout, newItem: GridItemLayout) => void;
  /** Обработчик начала resize */
  onResizeStart: (currentLayout: GridItemLayout[], oldItem: GridItemLayout) => void;
  /** Обработчик resize */
  onResize: (
    currentLayout: GridItemLayout[],
    oldItem: GridItemLayout,
    newItem: GridItemLayout,
    placeholder: GridItemLayout
  ) => void;
  /** Обработчик окончания resize */
  onResizeStop: (
    currentLayout: GridItemLayout[],
    oldItem: GridItemLayout,
    newItem: GridItemLayout
  ) => void;
  /** Обработчик редактирования блока */
  onEditBlock: (id: string) => void;
  /** Обработчик клонирования блока */
  onCloneBlock: (id: string) => void;
  /** Обработчик удаления блока */
  onDeleteBlock: (id: string) => void;
  /** Обработчик открытия полного отчёта */
  onOpenFullReport: (component: DashboardComponent) => void;
  /** Полноэкранный режим */
  isFullscreen: boolean;
  /** ID выделенных блоков */
  selectedBlockIds?: Set<string>;
  /** Снять выделение со всех блоков */
  onDeselectAll?: () => void;
  /** Обработчик перетаскивания (вызывается при каждом движении) */
  onDrag?: (layout: GridItemLayout[], oldItem: GridItemLayout, newItem: GridItemLayout) => void;
}

/**
 * Компонент сетки дашборда.
 * Отображает блоки в drag-and-drop сетке с возможностью resize.
 */
const DashboardGrid: FC<DashboardGridProps> = ({
  dashboardComponents,
  layout,
  gridCols,
  gridWidth,
  calculatedHeight,
  scale,
  isDragging,
  allowResize,
  onLayoutChange,
  onDragStart,
  onDragStop,
  onResizeStart,
  onResize,
  onResizeStop,
  onEditBlock,
  onCloneBlock,
  onDeleteBlock,
  onOpenFullReport,
  isFullscreen,
  selectedBlockIds,
  onDeselectAll,
  onDrag,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef<number>(0);

  const autoScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const { x: mx, y: my } = mouseRef.current;

    let dx = 0;
    let dy = 0;

    if (my < rect.top + AUTOSCROLL_EDGE) {
      dy = -AUTOSCROLL_SPEED;
    } else if (my > rect.bottom - AUTOSCROLL_EDGE) {
      dy = AUTOSCROLL_SPEED;
    }

    if (mx < rect.left + AUTOSCROLL_EDGE) {
      dx = -AUTOSCROLL_SPEED;
    } else if (mx > rect.right - AUTOSCROLL_EDGE) {
      dx = AUTOSCROLL_SPEED;
    }

    if (dx !== 0 || dy !== 0) {
      container.scrollBy(dx, dy);
    }

    rafRef.current = requestAnimationFrame(autoScroll);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const onMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };

    document.addEventListener('mousemove', onMouseMove);
    rafRef.current = requestAnimationFrame(autoScroll);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      cancelAnimationFrame(rafRef.current);
    };
  }, [isDragging, autoScroll]);

  /** Клик по пустому пространству сетки — снять выделение */
  const handleContainerClick = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(`.${styles.gridItem}`)) {
        onDeselectAll?.();
      }
    },
    [onDeselectAll]
  );

  return (
    <div
      ref={containerRef}
      className={`${styles.gridContainer} ${isDragging ? styles.isDragging : ''}`}
      style={{
        height: isFullscreen ? 'calc(100vh - 60px)' : 'calc(100vh - 200px)',
      }}
      onClick={handleContainerClick}
    >
      <div
        className={styles.gridContent}
        style={{
          zoom: scale,
          width: `${gridWidth}px`,
          minWidth: `${gridWidth}px`,
          minHeight: `${calculatedHeight}px`,
        }}
      >
        <Responsive
          className="layout"
          layouts={{ lg: layout }}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: gridCols, md: gridCols, sm: gridCols, xs: gridCols, xxs: gridCols }}
          rowHeight={GRID_CONSTANTS.CELL_HEIGHT}
          width={Number(gridWidth) || 5000}
          onLayoutChange={onLayoutChange}
          isDraggable={true}
          isResizable={true}
          compactType={null}
          preventCollision={!allowResize && !(isDragging && selectedBlockIds && selectedBlockIds.size > 1)}
          draggableHandle="[data-testid='drag-handler']"
          draggableCancel={`.${styles.resizeHandle}`}
          resizeHandles={['se']}
          transformScale={scale}
          resizeHandle={
            <div className={styles.resizeHandle}>
              <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 2L2 10M10 6L6 10M10 10L10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
          }
          onDragStart={onDragStart}
          onDrag={onDrag}
          onDragStop={onDragStop}
          onResizeStart={onResizeStart}
          onResize={onResize}
          onResizeStop={onResizeStop}
          margin={[GRID_CONSTANTS.MARGIN, GRID_CONSTANTS.MARGIN]}
          containerPadding={[0, 0]}
        >
          {dashboardComponents.map((component) => (
            <div key={component.layoutData.i} className={styles.gridItem} data-block-id={component.layoutData.i}>
              <DashboardBlock
                component={component}
                isSelected={selectedBlockIds?.has(component.layoutData.i)}
                onEdit={onEditBlock}
                onClone={onCloneBlock}
                onDelete={onDeleteBlock}
                onOpenFullReport={onOpenFullReport}
              />
            </div>
          ))}
        </Responsive>
      </div>
    </div>
  );
};

export default memo(DashboardGrid);
